import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportDataPage } from "../import/ImportDataPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  getConfigurationStoreKey,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import { AgendaPage } from "../agenda/AgendaPage.tsx";
import { ParametrizacaoPage } from "../parametrizacao/ParametrizacaoPage.tsx";
import { ApiPlayground } from "../settings/ApiPlayground.tsx";
import { ApiDocs } from "../settings/ApiDocs.tsx";
import { AdminOnlyRoute } from "../misc/AdminOnlyRoute.tsx";
import { CRMProvider } from "../context/CRMContext.tsx";
import { SuperAdminOnlyRoute } from "../misc/SuperAdminOnlyRoute.tsx";
import { CrmManagementPage } from "../admin/CrmManagementPage.tsx";
import { SuperAdminPortal } from "../admin/SuperAdminPortal.tsx";
import { ActivateCrmPage } from "../login/ActivateCrmPage.tsx";
import { GoalsPage } from "../goals/GoalsPage.tsx";
import { VoipProvider } from "../voip/VoipContext.tsx";
import { DotusWaCallProvider } from "../voip/DotusWaCallContext.tsx";
import { AutomationsPage } from "../automations";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI — never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  useEffect(() => {
    if (typeof document !== "undefined" && title) {
      document.title = title;
    }
  }, [title]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    const storeKey = getConfigurationStoreKey();
    if (!store.getItem(storeKey)) {
      store.setItem(storeKey, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(getConfigurationStoreKey(), config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(getConfigurationStoreKey(), config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(getConfigurationStoreKey());
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <CRMProvider>
      <VoipProvider>
        <DotusWaCallProvider>
          <ResponsiveAdmin
            dataProvider={dataProvider}
            authProvider={wrappedAuthProvider}
            i18nProvider={i18nProvider}
            store={store}
            loginPage={StartPage}
            requireAuth
            disableTelemetry
            title={title}
            {...rest}
          />
        </DotusWaCallProvider>
      </VoipProvider>
    </CRMProvider>
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const desktopQueryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 2,
          },
        },
      }),
    [],
  );

  return (
    <Admin
      queryClient={props.queryClient ?? desktopQueryClient}
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        <Route path="/activate-crm" element={<ActivateCrmPage />} />
        <Route path="/superadmin" element={<SuperAdminPortal />} />
        <Route path="/superadmin/*" element={<SuperAdminPortal />} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route
          path="/admin/crms"
          element={
            <SuperAdminOnlyRoute>
              <CrmManagementPage />
            </SuperAdminOnlyRoute>
          }
        />
        <Route
          path={SettingsPage.path}
          element={
            <AdminOnlyRoute resource="configuration">
              <SettingsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/configuration"
          element={
            <AdminOnlyRoute resource="configuration">
              <SettingsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/configuracoes"
          element={
            <AdminOnlyRoute resource="configuration">
              <SettingsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/configuracao"
          element={
            <AdminOnlyRoute resource="configuration">
              <SettingsPage />
            </AdminOnlyRoute>
          }
        />
        <Route path={ImportDataPage.path} element={<ImportDataPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        <Route path="/agenda" element={<AgendaPage />} />
        <Route path="/metas" element={<GoalsPage />} />
        <Route
          path="/automacoes"
          element={
            <AdminOnlyRoute resource="configuration">
              <AutomationsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/automations"
          element={
            <AdminOnlyRoute resource="configuration">
              <AutomationsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/parametrizacao"
          element={
            <AdminOnlyRoute resource="parametrizacao">
              <ParametrizacaoPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path={ApiPlayground.path}
          element={
            <AdminOnlyRoute resource="api">
              <ApiPlayground />
            </AdminOnlyRoute>
          }
        />
        <Route
          path={ApiDocs.path}
          element={
            <AdminOnlyRoute resource="api">
              <ApiDocs />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/api-documentation"
          element={
            <AdminOnlyRoute resource="api">
              <ApiDocs />
            </AdminOnlyRoute>
          }
        />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
      <Resource name="pipelines" />
      <Resource name="pipeline_stages" />
      <Resource name="custom_field_definitions" />
      <Resource name="goals" />
      <Resource name="crms" />
      <Resource name="crm_members" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
          <Route path="/activate-crm" element={<ActivateCrmPage />} />
          <Route path="/superadmin" element={<SuperAdminPortal />} />
          <Route path="/superadmin/*" element={<SuperAdminPortal />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path="/admin/crms"
            element={
              <SuperAdminOnlyRoute>
                <CrmManagementPage />
              </SuperAdminOnlyRoute>
            }
          />
          <Route
            path={SettingsPageMobile.path}
            element={
              <AdminOnlyRoute resource="configuration">
                <SettingsPageMobile />
              </AdminOnlyRoute>
            }
          />
          <Route
            path="/configuration"
            element={
              <AdminOnlyRoute resource="configuration">
                <SettingsPageMobile />
              </AdminOnlyRoute>
            }
          />
          <Route
            path="/configuracoes"
            element={
              <AdminOnlyRoute resource="configuration">
                <SettingsPageMobile />
              </AdminOnlyRoute>
            }
          />
          <Route
            path="/configuracao"
            element={
              <AdminOnlyRoute resource="configuration">
                <SettingsPageMobile />
              </AdminOnlyRoute>
            }
          />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          <Route path="/agenda" element={<AgendaPage />} />
          <Route path="/metas" element={<GoalsPage />} />
          <Route
            path="/automacoes"
            element={
              <AdminOnlyRoute resource="configuration">
                <AutomationsPage />
              </AdminOnlyRoute>
            }
          />
          <Route
            path="/automations"
            element={
              <AdminOnlyRoute resource="configuration">
                <AutomationsPage />
              </AdminOnlyRoute>
            }
          />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" show={CompanyShow} />
        <Resource name="tasks" list={MobileTasksList} />
        <Resource name="goals" />
        <Resource name="crms" />
        <Resource name="crm_members" />
      </Admin>
    </PersistQueryClientProvider>
  );
};
